/*
 * Page for setting up and maintaining integration settings for QBO
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Paper } from '@mui/material';
import { Input, NativeSelect, Tab, Tabs, TextField, Table, TableHead, TableBody, TableRow, TableCell, Tooltip, Typography  } from '@mui/material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Apps, HelpOutline, People, Settings } from '@mui/icons-material';

import QBOIntegrationApps from './QBOIntegrationApps';
import QBOCustomers from './QBOCustomers';
import TabPageTitle from '../ui/TabPageTitle';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import AppLogo from '../ui/AppLogo';
import { format, startOfDay } from 'date-fns';

const abEnvironment = new AbEnvironment();
const IntegrationSetupQBOQuery = graphql`
query IntegrationSetupQBOQuery($accountDelegatedAuthsId: UUID!) {
  accountDelegatedAuthsTByAccountDelegatedAuthsId(accountDelegatedAuthsId: $accountDelegatedAuthsId) {
    appName
    authType
    appData
    effectiveDate
    appIntegrationTByAppIntegrationId {
      appDescription
      appLogo
    }
    accountDelegatedAuthsMapTsByAccountDelegatedAuthsId {
      totalCount
      nodes {
        accountDelegatedAuthsMapId
        mapName
        entityId
        entityName
        entityDescription
      }
    }
    accountTByAccountId {
      accountName
      paymentSourceTsByAccountId(condition: { psStatus: 0 }) {
        totalCount
        nodes {
          psId
          psStatus
          psType
          ccNum
          ccExpy
          ccBrand
          ccLast4
          extSource
          fiName
          achAccountNum
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
    tabroot: {
      flexGrow: 1,
      margin: 10,
      width: 'auto',
      borderBottom: '2px solid #dedede',
    },
    tabScrollButton:{
      flex: '0 0 0px'
    },
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    vendorSelect: {
      width: 420,
    },
    vendorNameField: {
      width: 360,
    },
    accountSelect: {
      width: 370,
    },
    codeField: {
      width: 100,
    },
    descField: {
      minWidth: 486,
    },
    groupPaper: {
      paddingTop: 16,
      paddingLeft: 16,
      paddingRight: 16,
      paddingBottom: 16,  
    },
    paper: {
        [theme.breakpoints.up('xs')]: {
          padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(3),
        },
        textAlign: 'left',
        color: theme.palette.text.secondary,
        maxWidth: 1200,
        marginTop: 16,
    },
    tableRowNoBorder: {
      border: 0,
      //height: 16
    },
    tableCellNoBorder: {
      border: 0,
      paddingTop: 8,
      paddingBottom: 8,
    },
    tableCellNoBorderLeft: {
      border: 0,
      width: 192,
      paddingLeft: 0,
      paddingTop: 8,
      paddingBottom: 8,
      paddingRight: 0
    },
    tableCellNoBorderMiddle: {
      border: 0,
      width: 300,
      paddingTop: 8,
      paddingBottom: 8,
    },
    tableCellFundingSource: {
      width: 208,
    },
    [theme.breakpoints.up('xs')]: {
      tabContainer: {
        padding: 5,
        overflow: 'hidden'
      },
      tableCellPadding: {
        paddingLeft: `6px`,
        paddingRight: `14px`,
      },
      tab: {
        fontSize: 'xx-small',
      },
      labelContainer: {
        paddingLeft: 4,
        paddingRight: 4,
      },
      label: {
        fontSize: 'xx-small',
      },
      labelWrapped: {
        fontSize: 'xx-small',
      },
      tabIcon: {
        fontSize: 24,
      }
    },
    [theme.breakpoints.up('sm')]: {
      tabContainer: {
        padding: 10,
        overflow: 'hidden'
      },
      tableCellPadding: {
        paddingLeft: `12px`,
        paddingRight: `28px`,
      },
      tab: {
        fontSize: 'x-small',
      },
      labelContainer: {
        paddingLeft: 8,
        paddingRight: 8,
      },
      label: {
        fontSize: 'x-small',
      },
      labelWrapped: {
        fontSize: 'x-small',
      },
      tabIcon: {
        fontSize: 24,
      }
    },
    [theme.breakpoints.up('md')]: {
      tabContainer: {
        padding: 20,
        overflow: 'hidden'
      },
      tableCellPadding: {
        paddingLeft: `24px`,
        paddingRight: `56px`,
      },
      tab: {
        fontSize: '0.75rem',
      },
      labelContainer: {
        paddingLeft: 12,
        paddingRight: 12,
      },
      label: {
        fontSize: '0.75rem',
      },
      labelWrapped: {
        fontSize: '0.75rem',
      },
      tabIcon: {
        fontSize: 24,
      }
    }    
});

function TabContainer(props) {
  const classes = props.classes;
  return <div className={classes.tabContainer}>{props.children}</div>;
}

class IntegrationSetupQBO extends Component {

  constructor(props) {
    super(props);

    this.state = {
      forceFetch:       0,
      tabValue:         0,
      vendor_list:      [],
      vendor_list_full: [],
      acct_list:        [],
      acct_list_cur:    [],
      acct_list_dir:    [],
      acct_list_exp:    [],
      acct_list_rev:    [],
      acct_list_bank:   [],
      acct_list_cc:     [],
      acct_list_eq:     [],
      vendor:           null,
      asset:            null,
      cogs:             null,
      saasfee:          null,
      // abfee:            null,
      saasrev:          null,
      servicerev:       null,
      incentiverev:     null,
      funding_sources:  null, // Array of nodes with fsId and map node
      effectiveDate:    null,
      lookup_error:     null
    };
  }

  componentDidMount() {
    // Load hook
    const { isAuthenticatedReseller, isAuthenticatedCustomer, needAssent, callPartnerApi, logError } = this.props.auth;
    if ((isAuthenticatedReseller() || isAuthenticatedCustomer()) && ! needAssent()) { 
  
      let payload = {
        operation: "QBOGetAccounts"
      };
  
      // Make value available
      let v_list      = [];
      let v_list_full = []; // Not retricted to appbind lookup
      let a_list      = []; // All accounts
      let a_list_cur  = []; // Asset / Other Current Asset
      let a_list_dir  = []; // Cost of Goods Sold
      let a_list_exp  = []; // Expense
      let a_list_rev  = []; // Revenue
      let a_list_bank = []; // Bank
      let a_list_cc   = []; // CC
      let a_list_eq   = []; // Equity

      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
        if (response != null && response.body != null && response.body.body != null && response.body.body.QueryResponse != null) {
          a_list = response.body.body.QueryResponse.Account;

          for (let i=0; i<a_list.length; i++) {
            let theClassification = a_list[i].Classification;
            let theAccountSubType = a_list[i].AccountSubType;
            let theAccountType    = a_list[i].AccountType;
            //if (theClassification === 'Asset' && (theAccountSubType === 'OtherCurrentAssets' || theAccountType === 'Other Current Assets')) {
            if (theClassification === 'Asset' && (theAccountSubType === 'CashOnHand' || theAccountType === 'Bank')) {
              a_list_cur.push(a_list[i]);
            } else if (theClassification === 'Expense' && (theAccountSubType === 'Cost of Goods Sold' || theAccountType === 'Cost of Goods Sold')) {
              a_list_dir.push(a_list[i]);
            } else if (theClassification === 'Expense' && (theAccountSubType === 'Expense' || theAccountType === 'Expense')) {
              a_list_exp.push(a_list[i]);
            } else if (theClassification === 'Revenue') {
              a_list_rev.push(a_list[i]);
            }
            
            if (theAccountType === 'Equity') {
              a_list_eq.push(a_list[i]);
            } else if (theAccountType === 'Bank') {
              a_list_bank.push(a_list[i]);
            } else if (theAccountType === 'Credit Card') {
              a_list_cc.push(a_list[i]);
            }
          }    
        } else {
          // Throw
          throw(response);

        }
        // Now lookup AppBind vendor
        payload = {
          operation: "QBOGetVendor",
          params: {
            vendorName: 'AppBind'
          }
        }
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
      })
      .then(resp => {
        return resp.json();
      })
      .then(response => {
        if (response != null && response.body != null && response.body.body != null) {
            if (response.body.body.QueryResponse != null) {
                // Return is from a query
                if (response.body.body.QueryResponse.Vendor != null) {
                  v_list = response.body.body.QueryResponse.Vendor;
                }
            } else if (response.body.body.Vendor != null) {
                v_list.push(response.body.body.Vendor);
            }
        }

        // Now lookup all vendors
        payload = {
          operation: "QBOGetVendor",
          params: {
            maxRecords: 700
          }
        };

        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
      })
      .then(resp => {
        return resp.json();
      })
      .then(response => {

        if (response != null && response.body != null && response.body.body != null) {
          if (response.body.body.QueryResponse != null) {
              // Return is from a query
              if (response.body.body.QueryResponse.Vendor != null) {
                v_list_full = response.body.body.QueryResponse.Vendor;
              }
          } else if (response.body.body.Vendor != null) {
              v_list_full.push(response.body.body.Vendor);
          }
        }

        // If there is an AppBind asset account, set it on state
        let clearingId;
        let clearingName = '';
        for (let i=0; i<a_list_cur.length; i++) {
          let n = a_list_cur[i];
          if (n.DisplayName != null && n.DisplayName === "AppBind clearing") {
            clearingId = n.Id;
            clearingName = n.DisplayName;
          }
        }

        this.setState({vendor_list:      v_list,
                       vendor_list_full: v_list_full,
                       acct_list:        a_list,
                       acct_list_cur:    a_list_cur,
                       asset_id:         clearingId,
                       asset_name:       clearingName,
                       acct_list_dir:    a_list_dir,
                       acct_list_exp:    a_list_exp,
                       acct_list_rev:    a_list_rev,
                       acct_list_eq:     a_list_eq,
                       acct_list_bank:   a_list_bank,
                       acct_list_cc:     a_list_cc});
      })
      .catch(error => {
        logError('Exception in IntegrationSetupQBO.js: ' + JSON.stringify(error));
        this.setState({lookup_error: JSON.stringify(error)});
      });
    }
  }

  handleTabChange = (event, tabValue) => {
    this.setState({ tabValue });
  }

  disconnectFromApp(adaId) {
    const { callPartnerApi } = this.props.auth;

    if (! adaId) {
      return;
    }

    let payload = {
          operation: "OAuth2Revoke",
          params: {
            accountDelegatedAuthsId:  adaId
          }
        };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      if (data.link) {
        window.location.replace(data.link);
      } else {
        window.location.reload();
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  /**
   * Determine if a vendor name is already in use
   */
  doesVendorNameAlreadyExist(aName) {
    if (aName === '' || this.state.vendor_list == null || this.state.vendor_list.length === 0) {
      return false;
    }

    let found_match = false;
    for (let i=0; i<this.state.vendor_list.length; i++) {
      let n = this.state.vendor_list[i];
      if (n.DisplayName != null && n.DisplayName === aName) {
        found_match = true;
        break;
      }
    }

    return found_match;
  }

  /**
   * Determine if an account name already exists
   */
  doesAccountNameAlreadyExist(accountName) {
    if (accountName === '' || this.state.acct_list == null || this.state.acct_list.length === 0) {
      return false;
    }

    let found_match = false;
    for (let i=0; i<this.state.acct_list.length; i++) {
      let n = this.state.acct_list[i];
      if (n.Name != null && n.Name === accountName) {
        found_match = true;
        break;
      }
    }

    return found_match;
  }

  /*
   * Convenience function used in isSaveDisabled
   */
  isAccountStateValueInvalid(accountStateObject) {
    // For any state value that is not null, make sure that it is valid.
    // This means that for asset, cogs, abfee, and saas fee, etc, we can
    // select an existing account, but ensure that we aren't trying to create a duplicate.
    if (accountStateObject == null) {
      return false;
    }

    if (accountStateObject.entityDescription == null || accountStateObject.entityDescription === '') {
      return true;
    }

    // If there isn't an id, make sure that the name doesn't already exist
    if ((accountStateObject.entityId == null || accountStateObject.entityId === '') &&
        this.doesAccountNameAlreadyExist(accountStateObject.entityDescription)) 
    {
        return true;
    }
  }

  /*
   * Should save be disabled?  We only want it enabled if there are changes to save, and
   * all required values are set.  The parameters coming in will be blank if a new vendor
   * or account is to be created, and non-blank if an existing id is being used.
   * So if blank, require that additional fields are set.
   */
  // isSaveDisabled(vendorMap, assetMap, cogsMap, saasfeeMap, abfeeMap, saasRevMap, serviceRevMap, incentiveRevMap, fundingSources)
  isSaveDisabled(vendorMap, assetMap, cogsMap, saasfeeMap, saasRevMap, serviceRevMap, incentiveRevMap, fundingSources) {
    // console.log('In isSaveDisabled');
    // First, if all the state values are null, nothing has changed,
    // so disable.
    if (this.state.vendor     == null && this.state.asset == null &&
        this.state.cogs       == null && this.state.saasfee == null &&
        // this.state.abfee      == null && 
        this.state.saasrev    == null && this.state.servicerev == null && this.state.incentiverev == null &&
        this.state.funding_sources == null) {
      // console.log('isSaveDisabled: all null');
      return true;
    }

    // Next, do we have sufficient data? That is, we don't want
    // an incomplete set, so at a minimum, there must be a vendor, asset,
    // cogs, abfee, and saasfee mapping.  (Funding sources aren't required)
    //
    // Ensure that between state and mappings, sufficient data exists
    //
    if (this.state.vendor       == null && vendorMap == null)       return true;
    if (this.state.asset        == null && assetMap == null)        return true;
    if (this.state.cogs         == null && cogsMap == null)         return true;
    if (this.state.saasfee      == null && saasfeeMap == null)      return true;
    // if (this.state.abfee        == null && abfeeMap == null)        return true;
    if (this.state.saasrev      == null && saasRevMap == null)      return true;
    if (this.state.servicerev   == null && serviceRevMap == null)   return true;
    if (this.state.incentiverev == null && incentiveRevMap == null) return true;

    // accountDelegatedAuthsMapId

    // For any state value that is not null, make sure that it is valid.
    // This means that for vendor, asset, cogs, abfee, and saas fee, etc, we can
    // select an existing account, but ensure that we aren't trying to create a duplicate.
    if (this.state.vendor != null) {
      if (this.state.vendor.entityDescription == null || this.state.vendor.entityDescription === '')
        return true;

      // If there isn't an id, make sure that the name doesn't already exist
      if ((this.state.vendor.entityId == null || this.state.vendor.entityId === '') &&
          this.doesVendorNameAlreadyExist(this.state.vendor.entityDescription)) 
      {
          return true;
      }
    }

    if (this.isAccountStateValueInvalid(this.state.asset))        { return true; }
    if (this.isAccountStateValueInvalid(this.state.cogs))         { return true; }
    if (this.isAccountStateValueInvalid(this.state.saasfee))      { return true; }
    // if (this.isAccountStateValueInvalid(this.state.abfee))        { return true; }
    if (this.isAccountStateValueInvalid(this.state.saasrev))      { return true; }
    if (this.isAccountStateValueInvalid(this.state.servicerev))   { return true; }
    if (this.isAccountStateValueInvalid(this.state.incentiverev)) { return true; }

    // Finally, the intent is that state only has changes.  However,
    // there could be entries that were changed and then changed back.  So compare.
    //
    let changeFound = false;
    if      (this.state.vendor       != null && (vendorMap       == null || this.state.vendor.entityId       !== vendorMap.entityId      ) ) { changeFound = true; }
    else if (this.state.asset        != null && (assetMap        == null || this.state.asset.entityId        !== assetMap.entityId       ) ) { changeFound = true; }
    else if (this.state.cogs         != null && (cogsMap         == null || this.state.cogs.entityId         !== cogsMap.entityId        ) ) { changeFound = true; }
    else if (this.state.saasfee      != null && (saasfeeMap      == null || this.state.saasfee.entityId      !== saasfeeMap.entityId     ) ) { changeFound = true; }
    // else if (this.state.abfee        != null && (abfeeMap        == null || this.state.abfee.entityId        !== abfeeMap.entityId       ) ) { changeFound = true; }
    else if (this.state.saasrev      != null && (saasRevMap      == null || this.state.saasrev.entityId      !== saasRevMap.entityId     ) ) { changeFound = true; }
    else if (this.state.servicerev   != null && (serviceRevMap   == null || this.state.servicerev.entityId   !== serviceRevMap.entityId  ) ) { changeFound = true; }
    else if (this.state.incentiverev != null && (incentiveRevMap == null || this.state.incentiverev.entityId !== incentiveRevMap.entityId) ) { changeFound = true; }
    else {
      // Check funding sources
      if (this.state.funding_sources != null && fundingSources != null) {
        for (let i=0; i<this.state.funding_sources.length; i++) {
          let fsMapName  = this.state.funding_sources[i].mapName;
          let fsEntityId = this.state.funding_sources[i].entityId;
          for (let j=0; j<fundingSources.length; j++) {
            if (fsMapName === fundingSources[j].mapName) {
              // Potential change to an existing map
              if (fsEntityId !== fundingSources[j].mEntityId) {
                changeFound = true;
                break;
              }
            }
          }
          if (changeFound)
            break;
        }
      }
    }

    if (! changeFound) {
      return true;
    }

    return false;
  }

  copyAccountInfo(sourceObj, targetObj) {
    targetObj.accountDelegatedAuthsMapId = sourceObj.accountDelegatedAuthsMapId;
    targetObj.mapName                    = sourceObj.mapName;
    targetObj.entityId                   = sourceObj.entityId;
    targetObj.entityName                 = sourceObj.entityName;
    targetObj.entityDescription          = sourceObj.entityDescription;
  }

  hydrateAccount(mapName, entityName, propsObj, stateObj, stateList) {
    let mapObj = {
      accountDelegatedAuthsMapId: null,
      mapName: mapName,
      entityId: '',
      entityName: entityName,
      entityDescription: ''
    };

    // If there are state values, populate from state
    if (stateObj != null) {
      this.copyAccountInfo(stateObj, mapObj);
    } else if (propsObj != null) {
      this.copyAccountInfo(propsObj, mapObj);
    }

    if (mapObj.entityId != null && mapObj.entityId !== '') {
      let idMatch = false;
      for (let j=0; j<stateList.length; j++) {
        if (stateList[j].Id === mapObj.entityId) {
          idMatch = true;
          break;
        }
      }
      if (idMatch === false) {
        mapObj.entityId = '';
      }
    }
    return mapObj;
  }

  setEffectiveDate(aDate) {
    // Set date to the beginning of the day.
    let theDate = startOfDay(aDate);
    this.setState({effectiveDate: theDate});
  }

  getEffectiveDate(propsEffectiveDate) {
    if (this.state.effectiveDate != null) {
      return this.state.effectiveDate;
    } else if (propsEffectiveDate != null) {
      let aDate = null;
      try {
        aDate = new Date(propsEffectiveDate);
      } catch (anErr) {

      }
      if (aDate != null) {
        return aDate;
      }
    }

    return new Date();
  }

  /**
   * Return text as a 'YYYY-MM-DD' string.
   * 
   * This will either be a date on the state, or a date string from props.
   */
  getEffectiveDateString(propsEffectiveDate) {
    let theDate = this.getEffectiveDate(propsEffectiveDate);
    let theEffectiveDateText = format(theDate, 'yyyy-MM-dd');
    return theEffectiveDateText;
  }

  /***************************************
   * Generic set/get convenience methods
   **************************************/
   selectMappedEntry(stateAttrName, validValueList, anId, currentObj) {
    //console.log('selectMappedEntry called with name ' + stateAttrName + ', id ' + anId + ', currentObj: ' + JSON.stringify(currentObj));
    let mapObj = {
      accountDelegatedAuthsMapId: currentObj.accountDelegatedAuthsMapId,
      mapName: currentObj.mapName,
      entityId: currentObj.entityId,
      entityName: currentObj.entityName,
      entityDescription: currentObj.entityDescription
    };

    if (anId != null && anId !=='') {
      mapObj.entityId = anId;

      // Lookup the cogs account entry from the list matching on id
      for (let i=0; i<validValueList.length; i++) {
        let theAcct = validValueList[i];
        if (theAcct.Id === anId) {
          if (stateAttrName === 'vendor') {
            mapObj.entityDescription = theAcct.DisplayName;
          } else {
            mapObj.entityDescription = theAcct.Name;
          }
          break;
        }
      }
    } else {
      mapObj.entityId = '';
      mapObj.entityDescription = '';
    }
    this.setState({[stateAttrName]: mapObj});
  }

  getMappedName(stateAttrName) {
    if (this.state[stateAttrName] != null) {
      return this.state[stateAttrName].entityDescription;
    }
    return "";
  }

  setMappedName(stateAttrName, currentObj, name) {
    currentObj.entityDescription = name;
    this.setState({[stateAttrName]: currentObj});
  }

  /**********************************************
   * Funding source account convenience methods
   *********************************************/
  selectFundingSourceEntry(qboEntityId, fsType, fs) {
    let fsource = {
      accountDelegatedAuthsMapId: fs.accountDelegatedAuthsMapId,
      mapName: fs.mapName,
      entityId: fs.entityId,
      entityName: fs.entityName,
      entityDescription: fs.entityDescription
    };

    if (qboEntityId != null && qboEntityId !== '') {
      fsource.entityId = qboEntityId;

      if (fsType === 'cc') {
        // Lookup the cc entry from the list matching in id
        for (let i=0; i<this.state.acct_list_cc.length; i++) {
          let theAcct = this.state.acct_list_cc[i];
          if (theAcct.Id === qboEntityId) {
            fsource.entityDescription = theAcct.Name;
            break;
          }
        }
      } else {
        // Lookup the bank entry from the list matching in id
        for (let i=0; i<this.state.acct_list_bank.length; i++) {
          let theAcct = this.state.acct_list_bank[i];
          if (theAcct.Id === qboEntityId) {
            fsource.entityDescription = theAcct.Name;
            break;
          }
        }
      }
    } else {
      fsource.entityId = '';
      fsource.entityDescription = '';
    }

    let new_funding_sources = [];
    let fs_added = false;
    if (this.state.funding_sources != null) {
      for (let i=0; i<this.state.funding_sources.length; i++) {
        let aSource = this.state.funding_sources[i];
        // Need to recreate the state.
        if (aSource.mapName === fsource.mapName) {
          fs_added = true;
          new_funding_sources.push(fsource);
        } else {
          new_funding_sources.push(aSource);
        }
      }
    }

    if (! fs_added) {
      new_funding_sources.push(fsource);
    }

    this.setState({funding_sources: new_funding_sources});
  }

  clearChanges() {
    this.setState({vendor: null, asset: null, cogs: null, saasfee: null, saasrev: null, servicerev: null, incentiverev: null, funding_sources: null});
  }

  /* Call API to save settings, as this may involve api calls to push entities to the integrated app */
  // saveSettings(adaId, propsEffectiveDate) {
  saveSettings(adaId) {
    const { callPartnerApi } = this.props.auth;

    // To make an initial balance journal entry for the clearing, we'll need to pass in the id for
    // the corresponding opening balance equity account.  So look for an appropriate equity account in the 
    // list of equity accounts.
    let openingBalanceEquityAccount = null;
    for (let j=0; j<this.state.acct_list_eq.length; j++) {
      let eqAcct = this.state.acct_list_eq[j];
      if (eqAcct.AccountSubType === 'OpeningBalanceEquity') {
        openingBalanceEquityAccount = eqAcct;
        break;
      }
    }

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "QBOUpdateMappings",
      params: {
        accountDelegatedAuthsId:  adaId,
        effectiveDate: null,
        openingBalanceEquityAccount: openingBalanceEquityAccount,
        maps: []
      }
    };
    // effectiveDate: this.getEffectiveDateString(propsEffectiveDate),

    // The payload should only contain changes.  This means that
    // values in the state variables are what should be sent.
    let maps = [];
    if (this.state.vendor != null)       { maps.push(this.state.vendor); }
    if (this.state.asset != null)        { maps.push(this.state.asset); }
    if (this.state.cogs != null)         { maps.push(this.state.cogs); }
    // if (this.state.abfee != null)        { maps.push(this.state.abfee); }
    if (this.state.saasfee != null)      { maps.push(this.state.saasfee); }
    if (this.state.saasrev != null)      { maps.push(this.state.saasrev); }
    if (this.state.servicerev != null)   { maps.push(this.state.servicerev); }
    if (this.state.incentiverev != null) { maps.push(this.state.incentiverev); }

    if (this.state.funding_sources != null) {
      for (let i=0; i<this.state.funding_sources.length; i++) {
        maps.push(this.state.funding_sources[i]);
      }
    }

    if (maps.length > 0) {
      payload.params.maps = maps;
    }

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      if (data.link) {
        window.location.replace(data.link);
      } else {
        window.location.reload();
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  updateForceFetch = () => {
    let temp = this.state.forceFetch;
    temp++;
    this.setState({forceFetch: temp});
  }

  render() {
    const { isAuthenticatedReseller, isAuthenticatedCustomer, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;
    const { tabValue } = this.state;

    if ((!isAuthenticatedReseller() && !isAuthenticatedCustomer()) || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }

    const params = new URLSearchParams(this.props.location.search);
    let adaId = params.get('adaId');

    let backText = "Settings";

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={IntegrationSetupQBOQuery}
        variables={{accountDelegatedAuthsId: adaId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let backLink = 'settings';
            if (props.accountDelegatedAuthsTByAccountDelegatedAuthsId === null)
            {              
              return (
                <PageTitle title="Integration not found" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
              );
            }

            // If the state hasn't loaded yet, show spinner...
            if (this.state.acct_list == null || this.state.acct_list.length === 0) {
              if (this.state.lookup_error != null) {
                return (
                  <PageTitle title="Unable to load integration" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
                );
              }
              return (
                <LoadingSpinner />
              );
            }

            // Are there any mappings?
            let theIntegration = props.accountDelegatedAuthsTByAccountDelegatedAuthsId;
            let theDescription = theIntegration.appIntegrationTByAppIntegrationId.appDescription;
            let theLogo        = theIntegration.appIntegrationTByAppIntegrationId.appLogo;

            //let theTitle = theIntegration.appName + ' integration';
            if (theIntegration.appData && theIntegration.appName === 'QuickBooks') {
              // let theRealm = theIntegration.appData.realmId;
              let companyName = theIntegration.appData.CompanyInfo[0].CompanyName;
              theDescription = theDescription + ' ' + companyName;
            } else {
              return (
                <div className={classes.root}>
                  <PageTitle title="Unable to load integration" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
                  <div>
                    <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId)}}>Disconnect from Xero</Button>
                  </div>
                </div>
              );
            }

            let theMaps = theIntegration.accountDelegatedAuthsMapTsByAccountDelegatedAuthsId;

            let appBindVendor       = null;
            let appBindAsset        = null;
            let subscriptionCost    = null;
            let subscriptionExpense = null; // was saasFeeAccount
            // let subscriptionFees    = null; // was appBindFeeAccount
            let subscriptionRevenue = null; // was saasRevAccount
            let servicesRevenue     = null; // was serviceRevAccount
            let incentiveRevenue    = null; // was incentiveRevAccount
            // let subscriptionItem    = null; // For subscription sales item lines; not currently maintained here, but hardocded
            // let serviceItem         = null; // For subscription sales item lines; not currently maintained here, but hardocded
            let fsMaps              = [];

            // Override mappings specific to apps
            let appVendorMaps           = [];
            let subscriptionExpenseMaps = [];
            let subscriptionCostMaps    = [];
            let subscriptionRevenueMaps = [];

            let mapsCount = theMaps.totalCount;

            //let theEffectiveDateText = this.getEffectiveDateString(theIntegration.effectiveDate);

            if (mapsCount > 0) {
              theMaps.nodes.forEach(n => {
                if (n.mapName === "AppBindVendor") {
                  appBindVendor = n;
                } else if (n.mapName === "AppBindAsset") {
                  appBindAsset = n;
                } else if (n.mapName === "SubscriptionCost") {
                  subscriptionCost = n;
                  subscriptionCostMaps.push(n);
                } else if (n.mapName === "SubscriptionExpense") {
                  subscriptionExpense = n;
                  subscriptionExpenseMaps.push(n);
                /* } else if (n.mapName === "SubscriptionFees") {
                  subscriptionFees = n; */
                } else if (n.mapName === "SubscriptionRevenue") {
                  subscriptionRevenue = n;
                  subscriptionRevenueMaps.push(n);
                } else if (n.mapName === "ServicesRevenue") {
                  servicesRevenue = n;
                } else if (n.mapName === "IncentiveRevenue") {
                  incentiveRevenue = n;
                /* } else if (n.mapName === "SubscriptionItem") {
                  subscriptionItem = n;
                } else if (n.mapName === "ServiceItem") {
                  serviceItem = n; */
                } else if (n.mapName.startsWith("FundingSource:") ) {
                  fsMaps.push(n);
                } else if (n.mapName.startsWith("AppVendor:")) {
                  appVendorMaps.push(n);
                } else if (n.mapName.startsWith("SubscriptionExpense:")) {
                  subscriptionExpenseMaps.push(n);
                } else if (n.mapName.startsWith("SubscriptionCost:")) {
                  subscriptionCostMaps.push(n);
                } else if (n.mapName.startsWith("SubscriptionRevenue:")) {
                  subscriptionRevenueMaps.push(n);
                }
              });
            }

            let theVendor = this.state.vendor;
            if (theVendor == null) {
              if (appBindVendor != null) {
                theVendor = appBindVendor;
              } else {
                theVendor = {
                  accountDelegatedAuthsMapId: null,
                  mapName: 'AppBindVendor',
                  entityId: '',
                  entityName: 'Vendor',
                  entityDescription: ''
                };
              }
            }

            let theAppBindAsset = this.hydrateAccount('AppBindAsset', 'Account', appBindAsset, this.state.asset, this.state.acct_list_cur);

            let theSubscriptionCost = this.hydrateAccount('SubscriptionCost', 'Account', subscriptionCost, this.state.cogs, this.state.acct_list_dir);
            let scNameError         = (theSubscriptionCost.entityId === '') && (this.state.cogs == null || this.doesAccountNameAlreadyExist(this.state.cogs.entityDescription));

            let theSubscriptionExpense = this.hydrateAccount('SubscriptionExpense', 'Account', subscriptionExpense, this.state.saasfee, this.state.acct_list_exp);
            let seNameError            = (theSubscriptionExpense.entityId === '') && (this.state.saasfee == null || this.doesAccountNameAlreadyExist(this.state.saasfee.entityDescription));

            /*
            let theSubscriptionFees = this.hydrateAccount('SubscriptionFees', 'Account', subscriptionFees, this.state.abfee, this.state.acct_list_exp);
            let sfNameError         = (theSubscriptionFees.entityId === '') && (this.state.abfee == null || this.doesAccountNameAlreadyExist(this.state.abfee.entityDescription));
            */

            let theSubscriptionRevenue = this.hydrateAccount('SubscriptionRevenue', 'Account', subscriptionRevenue, this.state.saasrev, this.state.acct_list_rev);
            let srNameError            = (theSubscriptionRevenue.entityId === '') && (this.state.saasrev == null || this.doesAccountNameAlreadyExist(this.state.saasrev.entityDescription));

            let theServicesRevenue = this.hydrateAccount('ServicesRevenue', 'Account', servicesRevenue, this.state.servicerev, this.state.acct_list_rev);
            let svrNameError       = (theServicesRevenue.entityId === '') && (this.state.servicerev == null || this.doesAccountNameAlreadyExist(this.state.servicerev.entityDescription));

            let theIncentiveRevenue = this.hydrateAccount('IncentiveRevenue', 'Account', incentiveRevenue, this.state.incentiverev, this.state.acct_list_rev);
            let irNameError         = (theIncentiveRevenue.entityId === '') && (this.state.incentiverev == null || this.doesAccountNameAlreadyExist(this.state.incentiverev.entityDescription));

            // items
            // - this is a bit different, as we're hardcoding the items.  They will depend on subscription revenue and sevices revenue accounts.
            //
            // let theSubscriptionItem = subscriptionItem;
            // let theServiceItem = serviceItem;

            // funding sources
            let theFundingSources = theIntegration.accountTByAccountId.paymentSourceTsByAccountId.nodes;
            let fundingSourcesWithMap = [];

            // This is more complex - we need to have sufficient
            // information to render each funding source and map.
            // There will be an entry for each funding source.
            //
            // Iterate through the funding sources
            // - is there already a map for that source?  If so,
            //   use the information for that map.
            // - if there isn't already a map, create new map
            //   information 
            //
            if (theFundingSources.length > 0) {
              for (let i=0; i<theFundingSources.length; i++) {
                let aSource = theFundingSources[i];
                let fsId = aSource.psId;
                let aMapName = 'FundingSource:' + fsId;
                
                // Is there a map for this source yet?
                let sourceWithMap = {
                  psId:              aSource.psId,
                  psStatus:          aSource.psStatus,
                  psType:            aSource.psType,
                  ccExpy:            aSource.ccExpy,
                  ccBrand:           aSource.ccBrand,
                  ccLast4:           aSource.ccLast4,
                  extSource:         aSource.extSource,
                  fiName:            aSource.fiName,
                  achAccountNum:     aSource.achAccountNum,
                  accountDelegatedAuthsMapId: null,
                  mapName: aMapName,
                  entityId: '',
                  mEntityId: '', // Version from the map
                  entityName: 'Account',
                  entityDescription: ''
                };

                // The map name for these entries is "FundingSource:<AppBind funding source id>"
                // - So we're showing that we mapping a specific appbind
                //   funding source to a QBO account.
                //

                // Map information will be a combination of state and map information.  State will only represent
                // what has changed, and map represents the initial (props) version.  So to determine
                // what to display, we need to look at both state and map information.  First look for a
                // match in state, and if no match is found, then look for a match in maps.
                //
                let matchFound = false;
                if (this.state.funding_sources != null && this.state.funding_sources.length > 0) {
                  for (let j=0; j<this.state.funding_sources.length; j++) {
                    let theFsState = this.state.funding_sources[j];
                    if (theFsState.mapName === aMapName) {
                      sourceWithMap.accountDelegatedAuthsMapId = theFsState.accountDelegatedAuthsMapId;
                      sourceWithMap.mapName = theFsState.mapName;
                      sourceWithMap.entityId = theFsState.entityId;
                      sourceWithMap.entityName = theFsState.entityName;
                      sourceWithMap.entityDescription = theFsState.entityDescription;
                      matchFound = true;
                      break;
                    }
                  }
                }

                // fsMaps is the list of maps that are for funding sources retrieved
                // via relay (all maps starting with "FundingSource:").
                for (let j=0; j<fsMaps.length; j++) {
                  let theFsMap = fsMaps[j];

                  if (theFsMap.mapName === aMapName) {
                    if (! matchFound) {
                      sourceWithMap.accountDelegatedAuthsMapId = theFsMap.accountDelegatedAuthsMapId;
                      sourceWithMap.mapName = theFsMap.mapName;
                      sourceWithMap.entityId = theFsMap.entityId;
                      sourceWithMap.entityName = theFsMap.entityName;
                      sourceWithMap.entityDescription = theFsMap.entityDescription;
                    }
                    sourceWithMap.mEntityId = theFsMap.entityId; // Version from the map
                  }
                }

                fundingSourcesWithMap.push(sourceWithMap);
              }
            }

            // Now the fundingSourcesWithMap has the map (if it exists),
            // and the QBO entity id (only if it is mapped)
            // - use the funding source id as the key for this.  Or just
            //   send the object?  

            // Index for funding source display
            let fsIdx = -1;

            return (
              <Grid container spacing={3} direction="row" justifyContent="flex-start">
                <Grid item xs={12}>
                  <PageTitle title="Manage integration" showBackButton={true} name={backText} backLink={backLink} noMargin={true} history={this.props.history}/>
                </Grid>
                <Grid item xs={12}>
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      <Grid item>
                        <AppLogo logoPath={theLogo} appName={theIntegration.appName} imageHeight='48px'/>
                      </Grid>
                      <Grid item>
                        <Typography variant="h6">{theIntegration.appName}</Typography>
                        <Typography variant="body2" className={classes.description}>{theDescription}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container spacing={0} direction="row" justifyContent="space-around">
                          <Grid item xs={12}>
                            <Tabs
                              value={tabValue}
                              onChange={this.handleTabChange}
                              indicatorColor="primary"
                              textColor="primary"
                              variant="fullWidth"
                              scrollButtons="auto"
                              classes={{root: classes.tabroot, scrollButtons: classes.tabScrollButton}}
                            >
                              <Tab label="Basics"
                                icon={<Settings className={classes.tabIcon} />}
                                classes={{
                                  root: classes.tab,
                                  wrapper: classes.labelWrapped,
                                }}
                              />
                              <Tab label="Vendor mappings"
                                icon={<Apps className={classes.tabIcon} />}
                                classes={{
                                  root: classes.tab,
                                  wrapper: classes.labelWrapped,
                                }}
                              />
                              <Tab label="Customer mappings"
                                icon={<People className={classes.tabIcon} />}
                                classes={{
                                  root: classes.tab,
                                  wrapper: classes.labelWrapped,
                                }}
                              />
                            </Tabs>
                          </Grid>   
                          <Grid item xs={12}>
                            {tabValue === 0 &&
                            <TabContainer {...this.props}>
                              <Grid container spacing={2} direction="row" justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <TabPageTitle title="Basic integration settings" subtitle="AppBind makes it easy to get your subscription and service transactions into QuickBooks."/>
                                </Grid>
                                {/*
                                <Grid item xs={12}>
                                  <Typography variant="h6">
                                    Effective date
                                  </Typography>
                                  <Typography variant="body2" className={classes.description}>
                                    When the AppBind / QBO integration is established, we'll create accounts in your connected QBO environment.  
                                    The asset account may already have a balance, and that balance will be brought into QBO as of the effective date. 
                                    We'll also only synchronize data that is entered on or after that date.
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  { mapsCount > 0 &&
                                  <Typography variant="body1">
                                    {theEffectiveDateText}
                                  </Typography>}

                                  { mapsCount === 0 &&
                                  <div style={AppBindStyles.datePickerField}>
                                  <DatePicker 
                                    maxDateMessage={AppBindStyles.datePickerMessageMax}
                                    autoOk
                                    label="Integration effective date"
                                    value={this.getEffectiveDate(theIntegration.effectiveDate)}
                                    onChange={(date) => this.setEffectiveDate(date) }
                                    format="YYYY-MM-DD"
                                    disableFuture
                                    renderInput={(params) => <TextField {...params} />}
                                  />
                                  </div>}
                                </Grid>
                                */}
                                <Grid item xs={12}>
                                  <Paper className={classes.groupPaper}>
                                    <Grid container spacing={2} direction="row" >
                                      <Grid item xs={12}>
                                        <Typography variant="h6">
                                          Set up AppBind in QBO
                                        </Typography>
                                        <Typography variant="body2" className={classes.description}>Create AppBind as a vendor in QBO and create a “clearing account” to reflect the balance you have in AppBind.</Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Table>
                                          <TableBody>
                                            <TableRow className={classes.tableRowNoBorder}>
                                              <TableCell className={classes.tableCellNoBorderLeft}>
                                                <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                                                  <Grid item>
                                                    <Typography variant="body1">AppBind vendor&nbsp;</Typography>
                                                  </Grid>
                                                  <Grid item>
                                                    <Tooltip placement='bottom' title="An AppBind vendor is needed in QBO in order to support billing transactions.">
                                                      <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}} />
                                                    </Tooltip>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                              <TableCell className={classes.tableCellNoBorderMiddle}>
                                                <NativeSelect
                                                  value={theVendor.entityId}
                                                  input={<Input name="vendor" id="vendor" />}
                                                  onChange={(e) => this.selectMappedEntry('vendor', this.state.vendor_list, e.target.value, theVendor) }
                                                >
                                                  {this.state.vendor_list.map(n => {
                                                    let theLabel = "Map to " + n.DisplayName;
                                                    if (theVendor.entityId === n.Id) {
                                                      theLabel = n.DisplayName;
                                                    }
                                                    return(<option key={n.Id} value={n.Id}>{theLabel}</option>);
                                                  })}
                                                  <option key="" value="">Create new vendor for AppBind</option>
                                                </NativeSelect>
                                              </TableCell>
                                              <TableCell className={classes.tableCellNoBorder}>
                                                {theVendor.entityId === '' &&
                                                <TextField
                                                  className={classes.vendorNameField}
                                                  id="vendor_name"
                                                  value={this.getMappedName('vendor')}
                                                  onChange={(e) => this.setMappedName('vendor', theVendor, e.target.value)} 
                                                  inputProps={{ maxLength: 150 }}
                                                />}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow className={classes.tableRowNoBorder}>
                                              <TableCell className={classes.tableCellNoBorderLeft}>
                                                <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                                                  <Grid item>
                                                    <Typography variant="body1">AppBind clearing account&nbsp;</Typography>
                                                  </Grid>
                                                  <Grid item>
                                                    <Tooltip placement='bottom' title="Your main wallet (account balance) in AppBind will be represented in QBO by a clearing account.">
                                                      <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}} />
                                                    </Tooltip>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                              <TableCell className={classes.tableCellNoBorderMiddle}>
                                                <NativeSelect
                                                    variant="outlined"
                                                    value={theAppBindAsset.entityId}
                                                    input={<Input name="Asset" id="asset" />}
                                                    onChange={(e) => this.selectMappedEntry('asset', this.state.acct_list_cur, e.target.value, theAppBindAsset) }
                                                  >
                                                    {this.state.acct_list_cur.map(n => {
                                                      return(<option key={n.Id} value={n.Id}>{n.Name}</option>);
                                                    })}
                                                    <option key="" value="">Create new clearing account for AppBind</option>
                                                </NativeSelect>
                                              </TableCell>
                                              <TableCell className={classes.tableCellNoBorder}>
                                                {theAppBindAsset.entityId === '' &&
                                                <TextField
                                                  className={classes.vendorNameField}
                                                  id="asset_name"
                                                  value={this.getMappedName('asset')}
                                                  multiline
                                                  disabled={theAppBindAsset.entityId !== ''}
                                                  onChange={(e) => this.setMappedName('asset', theAppBindAsset, e.target.value)} 
                                                  inputProps={{ maxLength: 100 }}
                                                />}
                                              </TableCell>
                                            </TableRow>
                                          </TableBody>
                                        </Table>

                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                  <Paper className={classes.groupPaper}>
                                    <Grid container spacing={2} direction="row" >
                                      <Grid item xs={12}>
                                        <Typography variant="h6">
                                          Map funding accounts
                                        </Typography>
                                        <Typography variant="body2" className={classes.description}>When you fund your AppBind wallet from a funding source, or payout funds from AppBind, we’ll record a corresponding transaction in QBO.</Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Table>
                                          <TableHead style={AppBindStyles.tableHead}>
                                            <TableRow style={AppBindStyles.tableHeadRow}>
                                              <TableCell className={classes.tableCellFundingSource}>AppBind funding source</TableCell>
                                              <TableCell colSpan={2}>QBO account</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {fundingSourcesWithMap.map(n => {
                                              fsIdx++;
                                              let theType = n.psType;
                                              let theValue = n.entityId;
                                              let i_id = 'fs_' + fsIdx;
                                              if (theType === 0) {
                                                let ccDesc = n.ccBrand + ' (last four ' + n.ccLast4 + ')';
                                                return (
                                                  <TableRow key={i_id}>
                                                    <TableCell className={classes.tableCellFundingSource}><Typography variant="body1">{ccDesc}</Typography></TableCell>
                                                    <TableCell colSpan={2}>
                                                      <NativeSelect
                                                        value={theValue}
                                                        input={<Input name={i_id} id={i_id} />}
                                                        onChange={(e) => this.selectFundingSourceEntry(e.target.value, 'cc', n)}
                                                      >
                                                        {this.state.acct_list_cc.map(m => {
                                                          return(<option key={m.Id} value={m.Id}>{m.Name}</option>);
                                                        })}
                                                        <option key="" value="">Not mapped</option>
                                                      </NativeSelect>
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              } else if (theType === 1) {
                                                let achDesc = 'Bank account (' + n.fiName + ' ' + n.achAccountNum + ')';
                                                return (
                                                  <TableRow key={i_id}>
                                                    <TableCell className={classes.tableCellFundingSource}><Typography variant="body1">{achDesc}</Typography></TableCell>
                                                    <TableCell colSpan={2}>
                                                      <NativeSelect
                                                        value={theValue}
                                                        input={<Input name={i_id} id={i_id} />}
                                                        onChange={(e) => this.selectFundingSourceEntry(e.target.value, 'bank', n)}
                                                      >
                                                        {this.state.acct_list_bank.map(m => {
                                                          return(<option key={m.Id} value={m.Id}>{m.Name}</option>);
                                                        })}
                                                        <option key="" value="">Not mapped</option>
                                                      </NativeSelect>
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              } else if (theType === 3) {
                                                let achDesc ="Payout bank account";
                                                return (
                                                  <TableRow key={i_id}>
                                                    <TableCell className={classes.tableCellFundingSource}><Typography variant="body1">{achDesc}</Typography></TableCell>
                                                    <TableCell colSpan={2}>
                                                      <NativeSelect
                                                        value={theValue}
                                                        input={<Input name={i_id} id={i_id} />}
                                                        onChange={(e) => this.selectFundingSourceEntry(e.target.value, 'bank', n)}
                                                      >
                                                        {this.state.acct_list_bank.map(m => {
                                                          return(<option key={m.Id} value={m.Id}>{m.Name}</option>);
                                                        })}
                                                        <option key="" value="">Not mapped</option>
                                                      </NativeSelect>
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              } else {
                                                return null;
                                              }
                                            })}
                                          </TableBody>
                                        </Table>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>

                                <Grid item xs={12}>
                                  <Paper className={classes.groupPaper}>
                                    <Grid container spacing={2} direction="row" >
                                      <Grid item xs={12}>
                                        <Typography variant="h6">
                                          Record expenses
                                        </Typography>
                                        <Typography variant="body2" className={classes.description}>AppBind automatically tracks your subscription and services expenses, and records these expenses in QBO.</Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Table>
                                          <TableHead style={AppBindStyles.tableHead}>
                                            <TableRow style={AppBindStyles.tableHeadRow}>
                                              <TableCell className={classes.tableCellFundingSource}>AppBind expense type</TableCell>
                                              <TableCell colSpan={2}>QBO account</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {/* Subscription expense */}
                                            <TableRow>
                                              <TableCell className={classes.tableCellFundingSource}>
                                                <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                                                  <Grid item>
                                                    <Typography variant="body1">Subscription expense&nbsp;</Typography>
                                                  </Grid>
                                                  <Grid item>
                                                    <Tooltip placement='bottom' title="The subscription expense type is any expense that you are paying for yourself.  This includes subscriptions that you purchase for yourself, AppBind platform fees, and payment processing fees.">
                                                      <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}} />
                                                    </Tooltip>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                              <TableCell>
                                                <NativeSelect
                                                  value={theSubscriptionExpense.entityId}
                                                  input={<Input name="saasfee" id="saasfee" />}
                                                  onChange={(e) => this.selectMappedEntry('saasfee', this.state.acct_list_exp, e.target.value, theSubscriptionExpense) }
                                                >
                                                  {this.state.acct_list_exp.map(n => {
                                                    return(<option key={n.Id} value={n.Id}>{n.Name}</option>);
                                                  })}
                                                  <option key="" value="">Create a new account</option>
                                                </NativeSelect>
                                              </TableCell>
                                              <TableCell>
                                                {theSubscriptionExpense.entityId === '' &&
                                                <TextField
                                                  className={classes.descField}
                                                  id="saasfee_name" label="Name"
                                                  value={this.getMappedName('saasfee')}
                                                  onChange={(e) => this.setMappedName('saasfee', theSubscriptionExpense, e.target.value)} 
                                                  error={seNameError}
                                                  inputProps={{ maxLength: 100 }}
                                                />}
                                              </TableCell>
                                            </TableRow>

                                            {/* Subscription COGS */}
                                            <TableRow>
                                              <TableCell className={classes.tableCellFundingSource}>
                                                <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                                                  <Grid item>
                                                    <Typography variant="body1">Subscription COGS&nbsp;</Typography>
                                                  </Grid>
                                                  <Grid item>
                                                    <Tooltip placement='bottom' title="The subscription COGS expense type is any expense that you paid on behalf of a customer.">
                                                      <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}} />
                                                    </Tooltip>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                              <TableCell>
                                                <NativeSelect
                                                  value={theSubscriptionCost.entityId}
                                                  input={<Input name="cogs" id="cogs" />}
                                                  onChange={(e) => this.selectMappedEntry('cogs', this.state.acct_list_dir, e.target.value, theSubscriptionCost) }
                                                >
                                                  {this.state.acct_list_dir.map(n => {
                                                    return(<option key={n.Id} value={n.Id}>{n.Name}</option>);
                                                  })}
                                                  <option key="" value="">Create a new account</option>
                                                </NativeSelect>
                                              </TableCell>
                                              <TableCell>
                                                {theSubscriptionCost.entityId === '' &&
                                                <TextField
                                                  className={classes.descField}
                                                  id="cogs_name" label="Name"
                                                  value={this.getMappedName('cogs')}
                                                  onChange={(e) => this.setMappedName('cogs', theSubscriptionCost, e.target.value)} 
                                                  error={scNameError}
                                                  inputProps={{ maxLength: 100 }}
                                                />}
                                              </TableCell>
                                            </TableRow>

                                          </TableBody>
                                        </Table>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>

                                {/*
                                <Grid item xs={12}>
                                  <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                    AppBind fees
                                  </Typography>
                                  <Typography variant="body2" className={classes.description}>AppBind fees are associated with this account.</Typography>
                                </Grid>
                                <Grid item>
                                  <FormControl className={classes.accountSelect}>
                                    <InputLabel shrink htmlFor="abfee">AppBind fees</InputLabel>
                                    <NativeSelect
                                      value={theSubscriptionFees.entityId}
                                      input={<Input name="abfee" id="abfee" />}
                                      onChange={(e) => this.selectMappedEntry('abfee', this.state.acct_list_exp, e.target.value, theSubscriptionFees) }
                                    >
                                      {this.state.acct_list_exp.map(n => {
                                        return(<option key={n.Id} value={n.Id}>{n.Name}</option>);
                                    })}
                                      <option key="" value="">Create a new account</option>
                                    </NativeSelect>
                                  </FormControl>
                                </Grid>
                                {theSubscriptionFees.entityId === '' &&
                                <Grid item>
                                  <TextField
                                    className={classes.descField}
                                    id="abfee_name" label="Name"
                                    value={this.getMappedName('abfee')}
                                    onChange={(e) => this.setMappedName('abfee', theSubscriptionFees, e.target.value)} 
                                    error={sfNameError}
                                    inputProps={{ maxLength: 100 }}
                                  />
                                </Grid>}
                                */}

                                <Grid item xs={12}>
                                  <Paper className={classes.groupPaper}>
                                    <Grid container spacing={2} direction="row" >
                                      <Grid item xs={12}>
                                        <Typography variant="h6">
                                          Record revenue
                                        </Typography>
                                        <Typography variant="body2" className={classes.description}>AppBind automatically tracks the revenue you receive from services, markups, and commissions, and records this in QBO as income.</Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Table>
                                          <TableHead style={AppBindStyles.tableHead}>
                                            <TableRow style={AppBindStyles.tableHeadRow}>
                                              <TableCell className={classes.tableCellFundingSource}>AppBind revenue type</TableCell>
                                              <TableCell colSpan={2}>QBO account</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {/* Subscription revenue (markups) */}
                                            <TableRow>
                                              <TableCell className={classes.tableCellFundingSource}>
                                                <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                                                  <Grid item>
                                                    <Typography variant="body1">Subscription markups&nbsp;</Typography>
                                                  </Grid>
                                                  <Grid item>
                                                    <Tooltip placement='bottom' title="If you add a markup to a subscription that you provision for a customer, this revenue is categorized as subscription markup.">
                                                      <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}} />
                                                    </Tooltip>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                              <TableCell>
                                                <NativeSelect
                                                  value={theSubscriptionRevenue.entityId}
                                                  input={<Input name="saasrev" id="saasrev" />}
                                                  onChange={(e) => this.selectMappedEntry('saasrev', this.state.acct_list_rev, e.target.value, theSubscriptionRevenue) }
                                                >
                                                  {this.state.acct_list_rev.map(n => {
                                                    return(<option key={n.Id} value={n.Id}>{n.Name}</option>);
                                                })}
                                                  <option key="" value="">Create a new account</option>
                                                </NativeSelect>
                                              </TableCell>
                                              <TableCell>
                                                {theSubscriptionRevenue.entityId === '' &&
                                                <TextField
                                                  className={classes.descField}
                                                  id="saasrev_name" label="Name"
                                                  value={this.getMappedName('saasrev')}
                                                  onChange={(e) => this.setMappedName('saasrev', theSubscriptionRevenue, e.target.value)} 
                                                  error={srNameError}
                                                  inputProps={{ maxLength: 100 }}
                                                />}
                                              </TableCell>
                                            </TableRow>

                                            {/* Services revenue */}
                                            <TableRow>
                                              <TableCell className={classes.tableCellFundingSource}>
                                                <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                                                  <Grid item>
                                                    <Typography variant="body1">Services revenue&nbsp;</Typography>
                                                  </Grid>
                                                  <Grid item>
                                                    <Tooltip placement='bottom' title="Services billed to your customer through AppBind are categorized as services revenue.">
                                                      <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}} />
                                                    </Tooltip>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                              <TableCell>
                                                <NativeSelect
                                                  value={theServicesRevenue.entityId}
                                                  input={<Input name="servicerev" id="servicerev" />}
                                                  onChange={(e) => this.selectMappedEntry('servicerev', this.state.acct_list_rev, e.target.value, theServicesRevenue) }
                                                >
                                                  {this.state.acct_list_rev.map(n => {
                                                    return(<option key={n.Id} value={n.Id}>{n.Name}</option>);
                                                  })}
                                                  <option key="" value="">Create a new account</option>
                                                </NativeSelect>
                                              </TableCell>
                                              <TableCell>
                                                {theServicesRevenue.entityId === '' &&
                                                <TextField
                                                  className={classes.descField}
                                                  id="servicerev_name" label="Name"
                                                  value={this.getMappedName('servicerev')}
                                                  onChange={(e) => this.setMappedName('servicerev', theServicesRevenue, e.target.value)} 
                                                  error={svrNameError}
                                                  inputProps={{ maxLength: 100 }}
                                                />}
                                              </TableCell>
                                            </TableRow>

                                            {/* Incentive revenue */}
                                            <TableRow>
                                              <TableCell className={classes.tableCellFundingSource}>
                                                <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                                                  <Grid item>
                                                    <Typography variant="body1">Commissions&nbsp;</Typography>
                                                  </Grid>
                                                  <Grid item>
                                                    <Tooltip placement='bottom' title="Commissions you receive from partner programs fulfilled by AppBind are categorized as commission revenue.">
                                                      <HelpOutline fontSize="small" style={{verticalAlign: "-8%"}} />
                                                    </Tooltip>
                                                  </Grid>
                                                </Grid>
                                              </TableCell>
                                              <TableCell>
                                                <NativeSelect
                                                  value={theIncentiveRevenue.entityId}
                                                  input={<Input name="incentiverev" id="incentiverev" />}
                                                  onChange={(e) => this.selectMappedEntry('incentiverev', this.state.acct_list_rev, e.target.value, theIncentiveRevenue) }
                                                >
                                                  {this.state.acct_list_rev.map(n => {
                                                    return(<option key={n.Id} value={n.Id}>{n.Name}</option>);
                                                })}
                                                  <option key="" value="">Create a new account</option>
                                                </NativeSelect>
                                              </TableCell>
                                              <TableCell>
                                                {theIncentiveRevenue.entityId === '' &&
                                                <TextField
                                                  className={classes.descField}
                                                  id="incentiverev_name" label="Name"
                                                  value={this.getMappedName('incentiverev')}
                                                  onChange={(e) => this.setMappedName('incentiverev', theIncentiveRevenue, e.target.value)} 
                                                  error={irNameError}
                                                  inputProps={{ maxLength: 100 }}
                                                />}
                                              </TableCell>
                                            </TableRow>

                                          </TableBody>
                                        </Table>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>

                                <Grid item xs={12}/>

                                <Grid item xs={12}>
                                  <Grid container spacing={1} direction="row" justifyContent="space-between" alignItems="center">
                                    <Grid item>
                                      <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="center">
                                        <Grid item>
                                          <Button color="primary" 
                                                  variant="outlined" 
                                                  disabled={this.isSaveDisabled(appBindVendor, appBindAsset, subscriptionCost, subscriptionExpense, subscriptionRevenue, servicesRevenue, incentiveRevenue, fsMaps)} 
                                                  onClick={()=> { this.clearChanges() }}>
                                            Undo unsaved changes
                                          </Button>
                                        </Grid>
                                        <Grid item>
                                          <Button color="primary" 
                                                  variant="contained" 
                                                  disabled={this.isSaveDisabled(appBindVendor, appBindAsset, subscriptionCost, subscriptionExpense, subscriptionRevenue, servicesRevenue, incentiveRevenue, fsMaps)} 
                                                  onClick={()=> { this.saveSettings(adaId) }}>
                                            Save settings
                                          </Button>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    <Grid item>
                                      <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId)}}>Disconnect from QuickBooks</Button>
                                    </Grid>
                                  </Grid>
                                </Grid>

                                <Grid item>
                                </Grid>
                              </Grid>
                            </TabContainer>}
                            {tabValue === 1 &&
                            <TabContainer {...this.props}>
                              <QBOIntegrationApps 
                                  auth={this.props.auth}
                                  history={this.props.history}
                                  adaId={adaId}
                                  qboVendors={this.state.vendor_list_full}
                                  qboAccountsCogs={this.state.acct_list_dir}
                                  qboAccountsExpense={this.state.acct_list_exp}
                                  qboAccountsRevenue={this.state.acct_list_rev}
                                  appVendorMaps={appVendorMaps}
                                  costMaps={subscriptionCostMaps}
                                  expenseMaps={subscriptionExpenseMaps}
                                  revenueMaps={subscriptionRevenueMaps}
                                  forceParentQuery={this.updateForceFetch}
                              />
                            </TabContainer>}
                            {tabValue === 2 &&
                            <TabContainer {...this.props}>
                              <QBOCustomers 
                                  auth={this.props.auth}
                                  history={this.props.history}
                                  adaId={adaId}
                                  qboVendors={this.state.vendor_list_full}
                                  qboAccountsCogs={this.state.acct_list_dir}
                                  qboAccountsExpense={this.state.acct_list_exp}
                                  qboAccountsRevenue={this.state.acct_list_rev}
                                  appVendorMaps={appVendorMaps}
                                  costMaps={subscriptionCostMaps}
                                  expenseMaps={subscriptionExpenseMaps}
                                  revenueMaps={subscriptionRevenueMaps}
                                  forceParentQuery={this.updateForceFetch}
                              />
                            </TabContainer>}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            );
          }
          return (
            <LoadingSpinner />
          );
        }}
      />
    );
  }
}

IntegrationSetupQBO.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(IntegrationSetupQBO);
